import type { PriceInfo } from "epsm-api-types/ws";
import type {
	DatafeedConfiguration,
	DatafeedErrorCallback,
	GetMarksCallback,
	HistoryCallback,
	IBasicDataFeed,
	LibrarySymbolInfo,
	PeriodParams,
	ResolutionString,
	ResolveCallback,
	SearchSymbolsCallback,
	SubscribeBarsCallback,
	TimescaleMark,
} from "@/charting_library";
import { formatValueDate } from "@/components/widgets/helpers/FormattingHelpers";
import { SecuritiesService } from "@/components/widgets/Ticker/SecuritiesService";
import { compareStrings, ONE_DAY_IN_MS } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { chartColors } from "../../const";
import { TIMEFRAME_MAP } from "../../services/PriceService/PriceService";
import { Subcharts, Timeframes } from "../../types";
import {
	getIndicatorsService,
	type Indicator,
} from "./datafeed-services/getIndicatorsService";

export const ResolutionMap: Record<string, Timeframes> = {
	"1": Timeframes.ONE_MIN,
	"5": Timeframes.FIVE_MIN,
	"15": Timeframes.FIFTEEN_MIN,
	"60": Timeframes.ONE_HOUR,
	"1D": Timeframes.ONE_DAY,
	"1W": Timeframes.ONE_WEEK,
} as const;

export const getResolutionKeyByValue = (
	value: Timeframes,
): ResolutionString | undefined => {
	return Object.keys(ResolutionMap).find(
		(key) => ResolutionMap[key] === value,
	) as ResolutionString | undefined;
};

const configuration: DatafeedConfiguration = {
	supports_marks: false,
	supports_timescale_marks: true,
	supports_time: true,
	supported_resolutions: Object.keys(ResolutionMap) as ResolutionString[],
};

export const isTodayOrYesterday = (date: number): boolean => {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(today.getDate() - 1);

	const inputDate = new Date(date);

	return (
		inputDate >= yesterday &&
		inputDate <
			new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
	);
};

export class CustomDatafeed implements IBasicDataFeed {
	private symbols: LibrarySymbolInfo[] = [];
	private subscriptions = new Map<
		string,
		{
			lastPrice?: PriceInfo;
			eventSource: EventSource;
		}
	>();

	async onReady(callback: (config: DatafeedConfiguration) => void) {
		this.symbols = await SecuritiesService.create().getSymbols();
		setTimeout(() => {
			const exchanges: string[] = [];
			const symbolsTypes: string[] = [];
			for (const symbol of this.symbols) {
				if (!exchanges.includes(symbol.exchange)) {
					exchanges.push(symbol.exchange);
				}
				if (!symbolsTypes.includes(symbol.type)) {
					symbolsTypes.push(symbol.type);
				}
			}
			callback({
				...configuration,
				exchanges: [
					{ name: "All", value: "", desc: "All" },
					...exchanges.map((item) => ({
						name: item,
						value: item,
						desc: item,
					})),
				],
				symbols_types: [
					{ name: "All", value: "" },
					...symbolsTypes.map((item) => ({
						name: item,
						value: item,
					})),
				],
			});
		}, 0);
	}

	resolveSymbol(
		symbolName: string,
		onResolve: ResolveCallback,
		_onError: DatafeedErrorCallback,
	) {
		setTimeout(() => {
			const path = symbolName.split(":");
			let symbol: LibrarySymbolInfo | undefined;
			if (path.length === 2) {
				symbol = this.symbols.find(
					(item) =>
						!compareStrings(item.exchange, path[0]) &&
						!compareStrings(item.description, path[1]),
				);
			} else {
				symbol = this.symbols.find(
					(item) => !compareStrings(item.ticker, symbolName),
				);
			}

			if (symbol) {
				onResolve(symbol);
			} else {
				onResolve({
					ticker: symbolName,
					name: symbolName,
					description: symbolName,
					exchange: "",
					listed_exchange: "",
					type: "",
					session: "24x7",
					timezone: "America/New_York",
					minmov: 1,
					pricescale: 100,
					format: "price",
				});
			}
		}, 0);
	}

	getBars(
		symbolInfo: LibrarySymbolInfo,
		resolution: ResolutionString,
		periodParams: PeriodParams,
		onResult: HistoryCallback,
		onError: DatafeedErrorCallback,
	) {
		const from = periodParams.from * 1000 - 7 * ONE_DAY_IN_MS;
		let to = periodParams.to * 1000;
		if (resolution === "1D" && isTodayOrYesterday(to)) {
			to += ONE_DAY_IN_MS;
		}

		const [ticker, indicatorSuffix] = (symbolInfo.ticker ?? "").split(":");
		const indicatorsService = getIndicatorsService(
			indicatorSuffix as Indicator,
		);

		indicatorsService
			.getBarData(from, to, ResolutionMap[resolution], ticker)
			.then((data) => {
				onResult(data, { noData: !data.length });
			})
			.catch((err) => {
				ErrorHandler.logError(err);
				onError(err);
			});
	}

	getTimescaleMarks = async (
		symbolInfo: LibrarySymbolInfo,
		from: number,
		to: number,
		onDataCallback: GetMarksCallback<TimescaleMark>,
		resolution: ResolutionString,
	) => {
		if (resolution !== "1D") {
			onDataCallback([]);
			return;
		}

		const startDate = from * 1000;
		const endDate = to * 1000;

		const data = await getIndicatorsService(Subcharts.Powerdays).getBarData(
			startDate,
			endDate,
			ResolutionMap[resolution],
			symbolInfo.ticker || "",
		);

		const marks = data.flatMap((item) => {
			if (item.close) {
				const isPositive = item.close > 0;
				return [
					{
						id: item.time / 1000,
						time: item.time / 1000,
						color: isPositive ? chartColors.up : chartColors.down,
						label: "P",
						tooltip: [
							`${formatValueDate(new Date(item.time))}`,
							`${isPositive ? "Positive" : "Negative"} powerday ${item.close.toFixed(2)}`,
						],
					},
				];
			}
			return [];
		});

		onDataCallback(marks);
	};

	searchSymbols(
		userInput: string,
		exchange: string,
		symbolType: string,
		onResult: SearchSymbolsCallback,
	) {
		setTimeout(() => {
			const newSymbols = this.symbols.filter((symbol) => {
				const isExchangeValid = exchange === "" || symbol.exchange === exchange;
				const fullName = `${symbol.exchange}:${symbol.description}`;
				const isTypeValid = symbolType === "" || symbol.type === symbolType;
				const isFullSymbolContainsInput =
					fullName.toLowerCase().indexOf(userInput.toLowerCase()) !== -1 ||
					!compareStrings(symbol.ticker, userInput);
				return isExchangeValid && isFullSymbolContainsInput && isTypeValid;
			});
			onResult(newSymbols.map((item) => ({ ...item, symbol: item.ticker! })));
		}, 0);
	}

	subscribeBars(
		symbolInfo: LibrarySymbolInfo,
		resolution: ResolutionString,
		onRealtimeCallback: SubscribeBarsCallback,
		subscriberUID: string,
	) {
		const [ticker, indicatorSuffix] = (symbolInfo.ticker ?? "").split(":");
		if (indicatorSuffix) {
			return;
		}
		const timeFrame = ResolutionMap[resolution];
		const timeFrameInfo = TIMEFRAME_MAP[timeFrame];
		const eventSource = new EventSource(
			`${settings.wsHost}/live/${timeFrameInfo.wsType}/${ticker}`,
		);
		this.subscriptions.set(subscriberUID, { eventSource });
		const fraction = timeFrameInfo.duration;
		eventSource.onerror = (error) => {
			ErrorHandler.logError(error);
		};
		const processData = (data: PriceInfo) => {
			const time = Math.trunc(data.time / fraction) * fraction;
			onRealtimeCallback({ ...data, time });
		};
		eventSource.onmessage = (event) => {
			const items = JSON.parse(event.data) as PriceInfo[];
			for (const item of items) {
				const data = this.subscriptions.get(subscriberUID) ?? { eventSource };
				const lastPrice = data.lastPrice;
				if (!lastPrice || lastPrice.time <= item.time) {
					processData(item);
					data.lastPrice = item;
					this.subscriptions.set(subscriberUID, data);
				}
			}
		};
	}

	unsubscribeBars(subscriberUID: string) {
		const { eventSource } = this.subscriptions.get(subscriberUID) || {};
		if (eventSource) {
			eventSource.close();
			this.subscriptions.delete(subscriberUID);
		}
	}
}
